import React, { useEffect, useState } from 'react';
import { X, Bug, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v2.7 QoL — floating bug reporter. Toggle with Shift+B or from Settings.
// POSTs to /api/bug-report (best-effort). Falls back to copying JSON if endpoint 404.

export default function BugReporter() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT','TEXTAREA','SELECT'].includes(tag) && !open) return;
      if (e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        e.preventDefault();
        setOpen(v => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    // Expose a manual open for the Settings button
    window.__slop_openBugReporter = () => setOpen(true);
    return () => {
      window.removeEventListener('keydown', onKey);
      try { delete window.__slop_openBugReporter; } catch (e) {}
    };
  }, [open]);

  const payload = () => ({
    version: 'v2.7',
    ts: new Date().toISOString(),
    ua: navigator.userAgent,
    url: window.location.href,
    tag: (() => { try { return localStorage.getItem('friendslop.tag') || ''; } catch (e) { return ''; } })(),
    save: (() => { try { return !!localStorage.getItem('friendslop.save'); } catch (e) { return false; } })(),
    msg,
  });

  const send = async () => {
    const body = payload();
    try {
      await axios.post(`${API}/bug-report`, body, { timeout: 5000 });
      setSent(true);
    } catch (e) {
      // Fallback: copy JSON to clipboard so user can paste it in a Discord/issue.
      try { await navigator.clipboard.writeText(JSON.stringify(body, null, 2)); setCopied(true); } catch (er) {}
    }
    setTimeout(() => { setOpen(false); setSent(false); setCopied(false); setMsg(''); }, 1600);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="bug-reporter-modal">
      <div
        className="brutal-border bg-[#0a0a0a] max-w-md w-full p-5 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ borderColor: 'var(--red)', boxShadow: '6px 6px 0 var(--red)' }}
      >
        <button onClick={() => setOpen(false)} className="absolute top-2 right-2 text-[color:var(--text-dim)] hover:text-white" data-testid="bug-close">
          <X size={18}/>
        </button>
        <div className="flex items-center gap-2 mb-3">
          <Bug size={16} className="text-[color:var(--red)]"/>
          <h3 className="font-display font-black text-xl uppercase text-[color:var(--red)]">REPORT A BUG</h3>
        </div>
        <textarea
          className="w-full h-32 bg-[#050505] border-2 border-[color:var(--text-dim)] text-white p-2 font-mono text-sm"
          placeholder="What happened? What did you expect? Steps to reproduce…"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          data-testid="bug-report-textarea"
          autoFocus
        />
        <div className="text-[10px] uppercase font-mono text-[color:var(--text-dim)] mt-1">
          {msg.length}/1000 chars · Includes version, URL, tag & save state
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <button
            className="brutal-btn text-xs px-4 py-2"
            onClick={send}
            disabled={!msg.trim() || sent || copied}
            data-testid="bug-send"
            style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
          >
            <Send size={12}/> {sent ? 'SENT' : copied ? 'COPIED TO CLIPBOARD' : 'SEND'}
          </button>
        </div>
        <div className="mt-2 text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">SHIFT+B to open · ESC to close</div>
      </div>
    </div>
  );
}
